import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
  Label,
} from '@roger/ui-components';
import {
  exportAdoptionReportToCSV,
  exportAdoptionReportToXLSX,
} from '../../utils/adoptionReportExport';
import { getAdoptionReport } from '../../services/rogerDocApi';
import {
  type AdoptionReportEntry,
  toAdoptionReportEntry,
} from '../../types/adoptionReport.types';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import VisitCompletionByClinicianReportHeader from './VisitCompletionByClinicianReportHeader';
import VisitCompletionByClinicianReportTable from './VisitCompletionByClinicianReportTable';

export interface VisitCompletionByClinicianRef {
  exportToCSV: () => void;
}

export interface VisitCompletionByClinicianProps {
  onExportRegistry?: (exportFunc: (() => void) | null) => void;
}

const VisitCompletionByClinician = forwardRef<
  VisitCompletionByClinicianRef,
  VisitCompletionByClinicianProps
>(({ onExportRegistry }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState<AdoptionReportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(() => {
    const saved = localStorage.getItem('adoptionReport_showBreakdown');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideNeverLoggedIn, setHideNeverLoggedIn] = useState(() => {
    const saved = localStorage.getItem('adoptionReport_hideNeverLoggedIn');
    return saved ? JSON.parse(saved) : false;
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [exportFormatDialogOpen, setExportFormatDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('xlsx');

  const requestIdRef = useRef(0);

  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setReportData([]);

    const fetchReportData = async () => {
      try {
        const data = await getAdoptionReport(fromDate, toDate);
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        const processedData = data.map(rawData =>
          toAdoptionReportEntry(rawData)
        );
        setReportData(processedData);
        showSuccess(`Loaded ${processedData.length} clinician records`);
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        console.error('Error loading adoption report:', err);
        setError('Failed to load report data. Please try again.');
        showError(
          'Report Failed',
          'Failed to load report data. Please try again.'
        );
        setReportData([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void fetchReportData();
    // showSuccess/showError are stable Zustand actions; omit from deps to avoid extra refetches
  }, [fromDate, toDate]);

  const getFilenameBase = () =>
    `roger_adoption_report_${new Date().toISOString().split('T')[0]}`;

  const exportAsCSV = () => {
    exportAdoptionReportToCSV(reportData, getFilenameBase());
  };

  const exportAsXLSX = async () => {
    try {
      await exportAdoptionReportToXLSX(reportData, getFilenameBase());
    } catch (err) {
      console.error('Error exporting adoption report to XLSX:', err);
      showError('Failed to export report. Please try again.');
    }
  };

  const exportToCSV = () => {
    setExportFormatDialogOpen(true);
  };

  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  useEffect(() => {
    if (onExportRegistry) {
      if (loading || reportData.length === 0) {
        onExportRegistry(null);
      } else {
        onExportRegistry(exportToCSV);
      }
    }
  }, [loading, reportData.length, onExportRegistry]);

  return (
    <>
      <Dialog
        open={exportFormatDialogOpen}
        onOpenChange={open => {
          setExportFormatDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export adoption report</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              Choose the file format you want to export.
            </p>
            <RadioGroup
              value={exportFormat}
              onValueChange={value =>
                setExportFormat(value === 'xlsx' ? 'xlsx' : 'csv')
              }
              className='space-y-2'
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='xlsx' id='export-format-xlsx' />
                <Label htmlFor='export-format-xlsx' className='text-sm'>
                  Excel (.xlsx)
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='csv' id='export-format-csv' />
                <Label htmlFor='export-format-csv' className='text-sm'>
                  CSV (.csv)
                </Label>
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setExportFormatDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setExportFormatDialogOpen(false);
                if (exportFormat === 'xlsx') {
                  void exportAsXLSX();
                } else {
                  exportAsCSV();
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VisitCompletionByClinicianReportHeader
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        showBreakdown={showBreakdown}
        onShowBreakdownChange={setShowBreakdown}
        hideNeverLoggedIn={hideNeverLoggedIn}
        onHideNeverLoggedInChange={setHideNeverLoggedIn}
      />

      <VisitCompletionByClinicianReportTable
        data={reportData}
        loading={loading}
        error={error}
        showBreakdown={showBreakdown}
        hideNeverLoggedIn={hideNeverLoggedIn}
        searchTerm={searchTerm}
      />
    </>
  );
});

VisitCompletionByClinician.displayName = 'VisitCompletionByClinician';

export default VisitCompletionByClinician;
